/* System defined core library */
import { ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import * as moment from 'moment';

/* User defined library/components */
import { DemographicsService } from '../../../shared/services/demographics.service';
import { DynamicComponentService } from '../../../shared/services/dynamic.component.service';
import { RoutingService } from '../../../shared/services/routing.service';
import { TimerService } from '../../../shared/services/timer.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { OrchestrationApiService } from '../../shared/services/orchestration-api.service';
import { SchedulingApiService } from '../../shared/services/scheduling-api.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';

/* User defined models */
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { AppointmentSlot } from '../../../shared/models/scheduling/appointmentSlot.model';
import { Candidate } from '../../../shared/models/scheduling/candidate.model';
import { Client } from '../../../shared/models/scheduling/client.model';
import { Country } from '../../../shared/models/scheduling/country.model';
import { Hold } from '../../../shared/models/scheduling/hold.model';
import { MetricData, MetricDetails } from '../../../shared/models/scheduling/metrics.model';
import { Payment } from '../../../shared/models/scheduling/payment.model';
import { Program } from '../../../shared/models/scheduling/program.model';
import { SiteSlot } from '../../../shared/models/scheduling/siteSlot.model';
import { Slot } from '../../../shared/models/scheduling/slot.model';

/* User defined enums/constants */
import { ComponentType, MetricType, Fee } from '../../../shared/enums/scheduling.enum';
import { TestCenterSelecterComponent } from '../../shared/components/testcenter-selecter/testcenter-selecter.component';


@Component({
  selector: 'schedule-testcenter',
  templateUrl: './schedule-testcenter.component.html',
  styleUrls: ['./schedule-testcenter.component.scss'],
  providers: [DynamicComponentService]
})

export class ScheduleTestCenterComponent implements OnInit {
  @HostListener('window:popstate', ['$event'])
  @ViewChild('refreshavailabilitybtn') refreshBtn: ElementRef;
  @ViewChild(TestCenterSelecterComponent) testCenterSelecterComponentInstance : TestCenterSelecterComponent;

  onPopState(event: any) {
    this.clearDataOnGoingBack();
  }

  /* User defined private/public variables  */
  public candidate: Candidate;
  public client: Client;
  public countries: Country[] = [];
  public firstTimeLocationSearch: boolean[] = [];
  public holdSlotObj: Hold;
  public isBackToBackRequired: boolean = false;
  public isBiometricConsentRequiredSchedule: boolean = false;
  public IsCurrentFormChanged: boolean = false;
  public IsHoldInProgress: boolean = false;
  public isNextButtonVisible: boolean = true;
  public isSlotSelected: boolean = false;
  public metricsDetail: MetricDetails[];
  public prevSelectedHoldObj: any = [];
  public prevSelectedHoldObjArr: any = [];
  public prevSelectedSlot: Slot;
  public prevSelectionsArr: SiteSlot[] = [];
  public prevSites: any = [];
  public prevSlotSelections: any = [];
  public program: Program;
  public region: any;
  public selectedAppointments: AppointmentDetail[] = [];
  public selectedIndex: number = 0;
  public selectedSite: SiteSlot;
  public selectedStartDate: any;
  public hasConflictErrorOccured: boolean;
  public targetFlow: string;

  /* Service subscriptions */
  candidate$: Subscription;
  countries$: Subscription;
  client$: Subscription;
  firstTimeLocationSearch$: Subscription;
  hold$: Subscription;
  metricsDetail$: Subscription;
  prevSelectedHoldObj$: Subscription;
  prevSelectedSlot$: Subscription;
  prevSelectionsArr$: Subscription; // selected sites
  prevSites$: Subscription; // previous searched sites
  prevSlotSelections$: Subscription; // selected slots
  program$: Subscription;
  region$: Subscription;
  selectedAppointments$: Subscription;
  selectedSite$: Subscription;
  translateLanguage$: Subscription;
  selectedStartDate$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private demographicsService: DemographicsService,
    private dynamicComponentService: DynamicComponentService,
    private orchestrationApiService: OrchestrationApiService,
    private routingService: RoutingService,
    private schedulingApiService: SchedulingApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private timerService: TimerService,
    private translate: TranslateService,
    private utilityService: UtilityService,
    private cdRef: ChangeDetectorRef
  ) {
    this.candidate$ = this.selectedExamDetailService.candidate.subscribe(
      candidate => {
        this.candidate = candidate;
      });

    this.client$ = this.selectedExamDetailService.client.subscribe(
      client => {
        this.client = client;
      });

    this.countries$ = this.selectedExamDetailService.countries.subscribe(
      countries => {
        this.countries = countries;
      });

    this.firstTimeLocationSearch$ = selectedExamDetailService.firstTimeLocationSearch.subscribe(
      value => {
        let boolVal: boolean[] = [];
        boolVal.push(false);
        this.firstTimeLocationSearch = value == null ? boolVal : value;
      });

    this.hold$ = selectedExamDetailService.hold.subscribe(
      hold => {
        this.holdSlotObj = hold;
      });

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.metricsDetail$ = selectedExamDetailService.metricsDetail.subscribe(
      metricsDetail => {
        this.metricsDetail = metricsDetail;
      });

    // selected sites until now
    this.prevSelectionsArr$ = this.selectedExamDetailService.prevSelections.subscribe(
      siteSlots => {
        this.prevSelectionsArr = siteSlots || [];
      });

    this.prevSites$ = this.selectedExamDetailService.prevSites.subscribe(
      sites => {
        this.prevSites = sites || [];
      });

    this.prevSelectedHoldObj$ = selectedExamDetailService.prevSelectedHoldObj.subscribe(
      prevSelectedHoldObj => {
        this.prevSelectedHoldObj = prevSelectedHoldObj;
        if (this.prevSelectedHoldObj) {
          this.prevSelectedHoldObjArr = prevSelectedHoldObj;
        }
      });

    this.prevSelectedSlot$ = this.selectedExamDetailService.slot.subscribe(
      slot => {
        this.prevSelectedSlot = slot;
      });

    // selected slots until now
    this.prevSlotSelections$ = this.selectedExamDetailService.prevSlotSelections.subscribe(
      slots => {
        this.prevSlotSelections = slots || [];
      });

    this.program$ = this.selectedExamDetailService.program.subscribe(
      program => {
        this.program = program;
      });

    this.region$ = selectedExamDetailService.region.subscribe(
      region => {
        this.region = region;
      });

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });

    this.selectedSite$ = this.selectedExamDetailService.siteSlot.subscribe(
      siteSlot => {
        this.selectedSite = siteSlot;
      });

    this.selectedStartDate$ = this.selectedExamDetailService.selectedStartDate.subscribe(
      selectedStartDate => {
        this.selectedStartDate = selectedStartDate;
      });

    this.isBackToBackRequired = this.selectedExamDetailService.isBackToBackRequired;
    this.selectedIndex = this.selectedExamDetailService.selectedAppointmentIndex;
    this.isBiometricConsentRequiredSchedule = this.selectedExamDetailService.isBiometricConsentRequiredSchedule;
    this.targetFlow = this.selectedExamDetailService.currentTargetFlow;
  }

  /* Initialize the component & call required methods when component loaded first time. */
  ngOnInit() {
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.ScheduleTestCenterComponent)) return;
    if (this.prevSelectedSlot) {
      this.isSlotSelected = true;
    }

    this.selectedAppointments.forEach(appt => appt.exam.hasPayment = true);
  }

  /* Move back a page. */
  goToPrevPage() {
    this.clearDataOnGoingBack();
    this.routingService.backwardNavigation(ComponentType.ScheduleTestCenterComponent);
  }

  /* Use to clear details on navigating backward */
  clearDataOnGoingBack() {
    this.selectedExamDetailService.setSiteSlot(null);
    this.selectedExamDetailService.setSlot(null);
    this.selectedExamDetailService.setSiteSlots(null);
    this.selectedExamDetailService.setSelectedAvailableSlots(null);
    this.selectedExamDetailService.setSkipSize(null);

    this.timerService.clearClock();

    if (!this.isBackToBackRequired && this.selectedAppointments.length > 1) {
      this.selectedAppointments[this.selectedIndex].appointmentSlot = new AppointmentSlot();
      this.selectedAppointments[this.selectedIndex].hold = new Hold();
      this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
      if (this.prevSelectionsArr.length || this.prevSites.length) {
        this.prevSelectionsArr[this.selectedIndex] = null;
        this.prevSites[this.selectedIndex] = [];
      }
      if (this.prevSlotSelections.length) {
        this.prevSlotSelections[this.selectedIndex] = [];
      }
      this.selectedExamDetailService.setPrevSites(this.prevSites);
      this.selectedExamDetailService.setPrevSlotSelections(this.prevSlotSelections);
      this.selectedExamDetailService.setPrevSelections(this.prevSelectionsArr);
    }
    else {
      this.prevSites = [];
      this.prevSlotSelections = [];
      this.prevSelectionsArr = null;
      this.selectedExamDetailService.setPrevSites(this.prevSites);
      this.selectedExamDetailService.setPrevSlotSelections(this.prevSlotSelections);
      this.selectedExamDetailService.setPrevSelections(this.prevSelectionsArr);
      this.selectedAppointments.forEach((appointment) => {
        appointment.appointmentSlot = new AppointmentSlot();
        appointment.hold = new Hold();
      });
      this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
    }
  }

  /* Move to next page */
  goToNextPage() {
    this.holdOrSetDetail();
    this.getPaymentDetails();
  }

  // TODO - move these methods to service - KJC
  getPaymentDetails(): void {
    let totalAmount: number = 0;
    let currencyCode: string = '';
    let isPaymentRequired: boolean;

    this.selectedAppointments.forEach((appointmentDetail) => {
      appointmentDetail.payments = [];
      let country = appointmentDetail.appointmentSlot.site.location.address.country;
      this.schedulingApiService.getPaymentFeeDetail(appointmentDetail.exam.id, country).subscribe((paymentFee) => {
        paymentFee.forEach(payment => {
          if (payment.type == Fee.InitialScheduleFee) {
            let tmpPayment: Payment = new Payment(
              payment.amount,
              payment.currency,
              Fee.InitialScheduleFee
            );
            totalAmount += payment.amount;
            currencyCode = payment.currency;
            appointmentDetail.payments.push(tmpPayment);
            this.selectedExamDetailService.scheduleFee = {
              totalAmount: totalAmount,
              currency: currencyCode
            };

            isPaymentRequired = appointmentDetail.exam.hasPayment && totalAmount > 0;
            this.selectedExamDetailService.isPaymentRequired = isPaymentRequired;
            this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);

            if (!appointmentDetail.exam.hasPayment) {
              this.clearPaymentDetails(appointmentDetail.exam.id);
            }
          }
        });
      });
    });
  }

  // TODO - move these methods to service - KJC
  clearPaymentDetails(examId?: any) {
    examId = examId || this.selectedAppointments[this.selectedIndex].exam.id;
    let totalAmount = 0;
    let currencyCode = '';
    this.selectedAppointments.forEach((appointmentDetail) => {
      if (appointmentDetail.exam.id === examId) {
        appointmentDetail.payments.forEach((payment) => {
          if (payment) {
            payment.amount = 0;
          } else {
            let tmpPayment: Payment = new Payment(0);
            appointmentDetail.payments.push(tmpPayment);
          }
        });
      }
    });

    this.selectedAppointments.forEach(appt => {
      if (appt.payments.length) {
        totalAmount += appt.payments[0].amount;
        currencyCode = appt.payments[0].currency;
      }
    });

    this.selectedExamDetailService.scheduleFee = {
      totalAmount: totalAmount,
      currency: currencyCode
    };

    this.selectedExamDetailService.isPaymentRequired = this.selectedAppointments.some(appt => appt.exam.hasPayment) && totalAmount > 0;
    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
  }

  // TODO - move these methods to service - KJC
  setSettingsFromFlows(response: any, examId?: string) {
    const notificationResp = response.filter(function (el: any) {
      return el.name === "Verification";
    });

    this.selectedExamDetailService.showTextReminder = notificationResp[0].data &&
      notificationResp[0].data.length &&
      notificationResp[0].data[0].parameters.smsText === "True" ? true : false;

    const paymentResp = response.filter(function (el: any) {
      return el.name == "Payment";
    });

    examId = examId || this.selectedAppointments[this.selectedIndex].exam.id;

    this.selectedAppointments.forEach(appt => {
      if (appt.exam.id === examId) {
        appt.exam.hasPayment = paymentResp.length && paymentResp[0].settings.schedule === 'True';
      }
    });

    this.selectedExamDetailService.canSkipProfile = this.demographicsService.canSkipDemographicPage(response, this.candidate);

    let biometricResp = response.filter(function (el: any) {
      return el.name == "Biometric Consent";
    });

    // Exam Set does not return biometric as we call it
    if (biometricResp.length) {
      this.isBiometricConsentRequiredSchedule = (biometricResp.length && biometricResp[0].settings && biometricResp[0].settings.consent === 'True');
    }
    this.selectedExamDetailService.isBiometricConsentRequiredSchedule = this.isBiometricConsentRequiredSchedule;

    this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);

    if (this.selectedExamDetailService.canSkipProfile) {
      this.dynamicComponentService.setCandidateProfileforSkip(response);
    }

    this.selectedAppointments.forEach(appt => {
      if (!appt.exam.hasPayment) {
        this.clearPaymentDetails(appt.exam.id);
      }
    });
  }

  // TODO - move these methods to service - KJC
  holdOrSetDetail() {
    this.isSlotSelected = false; // Only need to disable the next button once clicked (No hide)
    this.IsHoldInProgress = true;

    if (this.holdSlotObj && this.prevSelectedHoldObj && this.prevSelectedHoldObj.length) {
      const commonHolds = this.prevSelectedHoldObj.filter((holdObj: any) => {
        return holdObj.slot.id === this.holdSlotObj.slot.id;
      })
      if (commonHolds.length) {
        this.routingService.scheduleFwdNavigation(ComponentType.ScheduleTestCenterComponent);
      } else {
        this.holdSlot(this.holdSlotObj);
      }
    } else {
      this.holdSlot(this.holdSlotObj);
    }
  }

  /* disable next button on the basis of local variable value */
  public isDisable(): boolean {
    return !this.isSlotSelected;
  }

  public setSelectedSiteDetails(selectedSiteDetails: any): void {
    this.selectedAppointments = selectedSiteDetails.selectedAppointments;
    this.selectedIndex = selectedSiteDetails.selectedIndex;
    this.holdSlotObj = selectedSiteDetails.holdSlotObj;
    this.hasConflictErrorOccured = false;
    this.metricsDetail = selectedSiteDetails.metricsDetail;
  }

  public setSlotsCheck(slotsCheck: any): void {
    this.isSlotSelected = slotsCheck;
    this.hasConflictErrorOccured = false;
  }

  refreshSlotAvailability(): void {
    this.testCenterSelecterComponentInstance.submitSearch();
  }

  // TODO - move these methods to service - KJC
  holdSlot(holdSlot: Hold): void {
    this.firstTimeLocationSearch$ = this.selectedExamDetailService.firstTimeLocationSearch.subscribe(value => {
      let boolVal: boolean[] = [];
      boolVal.push(false);
      this.firstTimeLocationSearch = value == null ? boolVal : value;
    });

    this.selectedIndex = this.selectedExamDetailService.selectedAppointmentIndex;

    if (this.isBackToBackRequired) {
      let requestMultipleHolds = {
        "slots": [{
          "id": holdSlot.slot.id
        }]
      }
      this.schedulingApiService.holdExamsBackToBackRequired(requestMultipleHolds).subscribe((holdsObjects: any) => {
        if (holdsObjects.length > 0) {

          if ((this.metricsDetail == undefined || this.metricsDetail == null || this.metricsDetail[0].searched == null || this.metricsDetail[0].searched == undefined) && this.firstTimeLocationSearch[0] == false) {
            this.setMetricsDetail(MetricType.Searched, null, this.selectedAppointments[0].hold.slot.startDateTime);
          }

          this.setMetricsDetail(MetricType.Selected, this.selectedAppointments[0].appointmentSlot.site.id, this.selectedAppointments[0].hold.slot.startDateTime, this.selectedAppointments[0].exam.id);
          this.setMetricsDetail(MetricType.BestDate);

          const selSlot = this.selectedAppointments[0].hold.slot;
          for (let count: number = 0; count < this.selectedAppointments.length; count++) {
            this.selectedAppointments[count].hold = holdsObjects[count];
            this.selectedAppointments[count].holdId = holdsObjects[count].id;

            if (count == 0) // only update id of first hold slot because it will be use when candidate goes previous for release hold if already selected slot choose.
            {
              holdSlot.id = holdsObjects[count].id;
              this.selectedExamDetailService.setHold(holdSlot);
              this.prevSelectedHoldObjArr.push(holdSlot);
              this.selectedExamDetailService.setprevSelectedHoldObj(this.prevSelectedHoldObjArr);
            }
          }
          // hack to maintain teh slot as it's being replaced in line 506
          // so that the slot selected will be shown when the user comes back
          this.selectedAppointments[0].hold.slot = selSlot;

          this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);

          // retrieving consent for first exam
          this.orchestrationApiService.getFlows(
            this.client.code,
            this.program.code,
            this.selectedAppointments[this.selectedIndex].exam.code,
            this.selectedSite.location.address.country
          ).subscribe((resp) => {
            this.setSettingsFromFlows(resp);
          });

          this.orchestrationApiService.getExamSetFlows(
            this.client.code,
            this.program.code,
            this.selectedExamDetailService.examSetId
          ).subscribe((resp) => {
            resp.forEach((flow: any) => {
              this.setSettingsFromFlows(flow.flowSet, flow.id);
              this.routingService.scheduleFwdNavigation(ComponentType.ScheduleTestCenterComponent);
            })
          })
        }
        else {
          if(holdsObjects == -2)
          {
            this.hasConflictErrorOccured = true;
            this.cdRef.detectChanges();
            this.isNextButtonVisible = true;
            this.isSlotSelected = false;
            this.refreshBtn.nativeElement.focus();
          }
          else {
            this.isNextButtonVisible = true;
            this.isSlotSelected = false;
          }
        }
      });
    }
    else {
      this.schedulingApiService.holdSiteSlot(this.selectedAppointments[this.selectedIndex].hold).subscribe((holdSlotId: any) => {
        if (holdSlotId > 0) {
          holdSlot.id = holdSlotId.toString();

          if ((this.metricsDetail == undefined || this.metricsDetail == null || this.metricsDetail[this.selectedIndex].searched == null || this.metricsDetail[this.selectedIndex].searched == undefined) && (this.firstTimeLocationSearch != null && this.firstTimeLocationSearch[this.selectedIndex] == false)) {
            this.setMetricsDetail(MetricType.Searched, null, this.selectedAppointments[this.selectedIndex].hold.slot.startDateTime);
          }
          this.setMetricsDetail(MetricType.Selected, this.selectedAppointments[this.selectedIndex].appointmentSlot.site.id, this.selectedAppointments[this.selectedIndex].hold.slot.startDateTime, this.selectedAppointments[this.selectedIndex].exam.id);
          this.setMetricsDetail(MetricType.BestDate);

          this.selectedAppointments[this.selectedIndex].hold.id = holdSlotId.toString();
          this.selectedAppointments[this.selectedIndex].holdId = holdSlot.id;
          this.selectedExamDetailService.setHold(holdSlot);
          this.prevSelectedHoldObjArr.push(holdSlot);
          this.selectedExamDetailService.setprevSelectedHoldObj(this.prevSelectedHoldObjArr);
          this.selectedAppointments[this.selectedIndex].appointmentSlot.id = this.holdSlotObj.slot.id;
          this.selectedAppointments[this.selectedIndex].appointmentSlot.href = this.holdSlotObj.slot.uri;
          this.selectedExamDetailService.SetSlotDataSource(this.selectedAppointments[this.selectedIndex].appointmentSlot);
          this.selectedExamDetailService.isRescheduleSlotSelected = true;

          // retrieving consent for first exam
          this.orchestrationApiService.getFlows(
            this.client.code,
            this.program.code,
            this.selectedAppointments[this.selectedIndex].exam.code,
            this.selectedSite.location.address.country
          ).subscribe((resp) => {
            this.setSettingsFromFlows(resp);
            this.routingService.scheduleFwdNavigation(ComponentType.ScheduleTestCenterComponent);
          });
        }
        else {
          if(holdSlotId == -2)
          {
            this.hasConflictErrorOccured = true;
            this.cdRef.detectChanges();
            this.isNextButtonVisible = true;
            this.isSlotSelected = false;
            this.refreshBtn.nativeElement.focus();
          }
          else {
            this.isNextButtonVisible = true;
            this.isSlotSelected = false;
          }
        }
      });
    }

  }

  /* Set metric details in shared service */
  private setMetricsDetail(metricType: MetricType, siteId?: string, slotdate?: any, examId?: string): void {
    const firstDateAvailable = moment(this.selectedSite.availability[0]).format("YYYY-MM-DDTHH:mm:ss");

    if (this.metricsDetail == null || this.metricsDetail == undefined) {
      this.metricsDetail = [];
      let metricDetails = new MetricDetails();
      this.metricsDetail.push(metricDetails);
    }
    else if (this.metricsDetail != null && this.selectedIndex > this.metricsDetail.length) {

      let metricDetails = new MetricDetails();
      this.metricsDetail.push(metricDetails);
    }

    if (metricType === MetricType.BestSite && this.region) {

      this.metricsDetail[this.selectedIndex].bestSite = new MetricData(
        MetricType.BestSite,
        { "latitude": this.region.lat, "longitude": this.region.long, "siteId": siteId },
        slotdate);
      this.selectedExamDetailService.setMetricsDetail(this.metricsDetail);
    }
    else if (metricType === MetricType.Selected && this.region) {
      this.metricsDetail[this.selectedIndex].selected = new MetricData(
        MetricType.Selected,
        { "latitude": this.region.lat, "longitude": this.region.long, "siteId": siteId },
        slotdate
      );
      this.metricsDetail[this.selectedIndex].examId = examId;
      this.selectedExamDetailService.setMetricsDetail(this.metricsDetail);
    }
    else if (metricType === MetricType.BestDate) {
      this.metricsDetail[this.selectedIndex].bestDate = new MetricData(
        MetricType.BestDate,
        {
          "searchedSiteId": this.selectedAppointments[this.selectedIndex].appointmentSlot.site.id,
          "offeredSiteId": this.selectedAppointments[this.selectedIndex].appointmentSlot.site.id,
          "searchedDate": this.utilityService.getFormatToMMDDYYYY(new Date(this.selectedStartDate.jsdate), "-")
        },
        this.utilityService.getFormatToMMDDYYYY(new Date(firstDateAvailable), "-")
      );
      this.selectedExamDetailService.setMetricsDetail(this.metricsDetail);
    }
    else if (metricType === MetricType.Searched && this.region) {
      this.metricsDetail[this.selectedIndex].searched = new MetricData(
        MetricType.Searched,
        { "latitude": this.region.lat, "longitude": this.region.long },
        this.utilityService.getFormatToMMDDYYYY(new Date(slotdate), "-")
      );

      this.selectedExamDetailService.setMetricsDetail(this.metricsDetail);

      if (!this.firstTimeLocationSearch[this.selectedIndex]) {
        this.firstTimeLocationSearch[this.selectedIndex] = true;
        this.selectedExamDetailService.setFirstTimeLocationSearch(this.firstTimeLocationSearch);

      } else {
        this.firstTimeLocationSearch[this.selectedIndex] = false;
        this.selectedExamDetailService.setFirstTimeLocationSearch(this.firstTimeLocationSearch);
      }
    }
  }
}
